export const PRE_FLIGHT_BRIEFING_EMPTY =
    'No pre flight briefing topics available.';

export function decodeBriefingBody(body) {
    if (typeof body !== 'string') return '';
    const trimmed = body.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('<')) return trimmed;

    try {
        const binary = atob(trimmed.replace(/\s/g, ''));
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const decoded = new TextDecoder().decode(bytes).trim();
        if (decoded.startsWith('<')) return decoded;
    } catch {
        return trimmed;
    }

    return trimmed;
}

const briefingId = (value) => {
    const id = Number(value);
    return Number.isInteger(id) ? id : null;
};

export function resolvePreFlightBriefing(saved, response) {
    const errorMsg =
        typeof response?.errorMsg === 'string' ? response.errorMsg.trim() : '';
    if (errorMsg) {
        return { action: 'keep', topic: saved || null };
    }

    const serverId = briefingId(response?.serverId);
    if (serverId === null || serverId < 0) {
        return { action: 'clear', topic: null };
    }

    const savedId = briefingId(saved?.id);
    if (savedId !== null && savedId === serverId) {
        return { action: 'keep', topic: saved };
    }

    const body = typeof response?.body === 'string' ? response.body.trim() : '';
    if (!body.startsWith('<')) {
        return { action: 'keep', topic: saved || null };
    }

    return {
        action: 'replace',
        topic: { id: serverId, html: body },
    };
}

export function sanitizeBriefingHtml(html) {
    if (typeof html !== 'string' || !html || typeof DOMParser === 'undefined') {
        return '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, iframe, object, embed').forEach((node) => {
        node.remove();
    });

    doc.body.querySelectorAll('*').forEach((node) => {
        for (const attr of [...node.attributes]) {
            if (attr.name.toLowerCase().startsWith('on')) {
                node.removeAttribute(attr.name);
            }
        }

        if (node.tagName === 'IMG') {
            const src = (node.getAttribute('src') || '').trim();
            if (!/^(data:|https?:)/i.test(src)) {
                node.removeAttribute('src');
            }
        }

        if (node.tagName === 'A') {
            const href = (node.getAttribute('href') || '').trim();
            if (!/^(https?:|mailto:)/i.test(href)) {
                node.removeAttribute('href');
            }
        }
    });

    return doc.body.innerHTML;
}

const readBlobAsDataUrl = (blob) =>
    new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () =>
            resolve(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
    });

export async function inlineBriefingImages(html) {
    if (typeof html !== 'string' || !html || typeof DOMParser === 'undefined') {
        return html || '';
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const images = [...doc.body.querySelectorAll('img')];

    await Promise.all(
        images.map(async (img) => {
            const src = (img.getAttribute('src') || '').trim();
            if (!/^https?:/i.test(src)) return;

            try {
                const response = await fetch(src);
                if (!response.ok) return;
                const dataUrl = await readBlobAsDataUrl(await response.blob());
                if (dataUrl) img.setAttribute('src', dataUrl);
            } catch (error) {
                console.error('Failed to inline briefing image', error);
            }
        })
    );

    return doc.body.innerHTML;
}

const UNSEEN_BRIEFING_KEY = 'preFlightBriefingUnseenId';
const KNOWN_BRIEFING_KEY = 'preFlightBriefingKnownId';
const BRIEFING_SESSION_ENDED_KEY = 'preFlightBriefingSessionEnded';

const storageGet = (store, key) => {
    try {
        return store.getItem(key);
    } catch {
        return null;
    }
};

const storageSet = (store, key, value) => {
    try {
        if (value == null) store.removeItem(key);
        else store.setItem(key, value);
    } catch {
        // storage can be blocked
    }
};

const storedBriefingId = (store, key) => {
    const id = Number(storageGet(store, key));
    return Number.isInteger(id) ? id : null;
};

export function readUnseenBriefingId() {
    return storedBriefingId(sessionStorage, UNSEEN_BRIEFING_KEY);
}

export function writeUnseenBriefingId(id) {
    storageSet(
        sessionStorage,
        UNSEEN_BRIEFING_KEY,
        Number.isInteger(id) ? String(id) : null
    );
}

export function readKnownBriefingId() {
    return storedBriefingId(localStorage, KNOWN_BRIEFING_KEY);
}

export function writeKnownBriefingId(id) {
    storageSet(
        localStorage,
        KNOWN_BRIEFING_KEY,
        Number.isInteger(id) ? String(id) : null
    );
}

export function shouldFlagBriefingNew(topicId, knownId, unseenId) {
    if (!Number.isInteger(topicId)) return false;
    if (topicId === unseenId) return true;
    return topicId !== knownId;
}

export function endBriefingNewSession() {
    writeUnseenBriefingId(null);
    storageSet(localStorage, BRIEFING_SESSION_ENDED_KEY, '1');
}

export function consumeEndedBriefingSession() {
    if (storageGet(localStorage, BRIEFING_SESSION_ENDED_KEY) !== '1') return;
    writeUnseenBriefingId(null);
    storageSet(localStorage, BRIEFING_SESSION_ENDED_KEY, null);
}
